import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ApiClientError, Table, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { wnfClient } from '../../api'
import type { PagedList, Wallet, WalletTransaction } from '../../api/types'
import { PageState } from '../../components/PageState'
import { TestFundsBanner } from '../../components/TestFundsBanner'
import { describeLoadError } from '../../lib/errors'
import { money, numericSortKey, utcDateTime } from '../../lib/format'
import { useDepositableCoins } from '../../lib/useDepositableCoins'
import { TransferCard } from './TransferCard'

const PAGE_SIZE = 200 // the legacy sheet fetched 200 rows in one go

const CAUSE_LABEL: Record<string, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  buy: 'BUY order',
  sell: 'SELL order',
}

const columns: Column<WalletTransaction>[] = [
  { key: 'createdAt', label: 'Date', align: 'left', format: (v) => utcDateTime(v as string) },
  { key: 'amount', label: 'Amount', numeric: true, cellClass: () => 'wnf-num', sortValue: (r) => numericSortKey(r.amount) },
  {
    key: 'result',
    label: 'Result',
    align: 'left',
    numeric: true,
    format: (v) => (v === 0 ? 'SUCCESS' : `Error code #${String(v)}`),
  },
  {
    key: 'balanceAfter',
    label: 'Balance after transaction',
    numeric: true,
    cellClass: () => 'wnf-num',
    format: (v) => money(v as string | number),
    sortValue: (r) => numericSortKey(r.balanceAfter),
  },
  { key: 'cause', label: 'Cause', align: 'left', format: (v) => CAUSE_LABEL[String(v)] ?? String(v) },
  { key: 'reference', label: 'Reference', align: 'left' },
  {
    key: 'metaJson',
    label: 'Meta',
    align: 'left',
    sortable: false,
    format: (v) => <pre className="wnf-meta">{String(v ?? '')}</pre>,
  },
]

/** The cursor for the next page, or null when this was the last one. */
function nextCursor(page: PagedList<WalletTransaction> | null): string | null {
  return page?.hasNext ? (page.next ?? null) : null
}

const isGone = (err: unknown) => err instanceof ApiClientError && err.status === 410

/** Legacy `WalletSheetComponent` (`:tradingType/wallets/:coin`): balance,
 * deposit / withdraw of test funds (any depositable coin, from the API) and
 * the transaction list. A coin without a wallet yet (`410`) that IS
 * depositable shows balance 0 and the deposit card, so a first deposit works. */
export function WalletSheetPage() {
  const { coin: rawCoin = '' } = useParams()
  const coin = rawCoin.toUpperCase()
  const wallet = useApiQuery(() => wnfClient.viewWallet(coin), [coin])
  const depositable = useDepositableCoins()
  const [txs, setTxs] = useState<WalletTransaction[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [txLoading, setTxLoading] = useState(true)
  const [txError, setTxError] = useState<unknown>(null)
  const [generation, setGeneration] = useState(0)

  useEffect(() => {
    let cancelled = false
    setTxLoading(true)
    setTxError(null)
    wnfClient
      .searchTransactions(coin, PAGE_SIZE, null)
      .then((page) => {
        if (cancelled) return
        setTxs(page.items ?? [])
        setCursor(nextCursor(page))
        setTxLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (isGone(err)) {
          // No wallet of this coin yet -> no transactions either.
          setTxs([])
          setCursor(null)
        } else {
          setTxError(err)
        }
        setTxLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [coin, generation])

  const loadMore = useCallback(async () => {
    if (!cursor) return
    try {
      const page = await wnfClient.searchTransactions(coin, PAGE_SIZE, cursor)
      setTxs((prev) => [...prev, ...(page.items ?? [])])
      setCursor(nextCursor(page))
    } catch (err) {
      setTxError(err)
    }
  }, [coin, cursor])

  const refresh = () => {
    wallet.refetch()
    setGeneration((g) => g + 1)
  }

  const transferable = depositable.find(coin)
  const noWallet = isGone(wallet.error)
  // A depositable coin the user holds no wallet of yet: show it empty.
  const walletData: Wallet | null =
    wallet.data ?? (noWallet && transferable ? { tradingType: 'spot', coin, balance: '0.00000000', lastModAt: null } : null)
  const walletError =
    wallet.error && !walletData && !(noWallet && depositable.loading)
      ? noWallet
        ? `You have no ${coin} wallet, and ${coin} cannot be deposited here.`
        : describeLoadError(wallet.error, 'the wallet')
      : null
  const walletLoading = wallet.loading || (noWallet && depositable.loading)

  return (
    <div>
      <div className="wnf-page-title">
        <h1>
          Wallet — spot — {coin}
        </h1>
        <Link to="/wallets">All wallets</Link>
      </div>
      <TestFundsBanner />

      <PageState loading={walletLoading} error={walletError}>
        {walletData && (
          <div className="wnf-sheet-top">
            <div className="wnf-card wnf-balance" data-testid="wallet-balance">
              <div className="wnf-muted">Balance (test funds)</div>
              <div className="wnf-balance__value wnf-num">
                {money(walletData.balance)} {coin}
              </div>
              {walletData.lastModAt && <div className="wnf-muted">Last mod at {utcDateTime(walletData.lastModAt)}</div>}
            </div>
            {transferable && <TransferCard kind="deposit" coin={coin} minAmount={transferable.minAmount} onDone={refresh} />}
            {transferable && <TransferCard kind="withdraw" coin={coin} minAmount={transferable.minAmount} onDone={refresh} />}
            {!transferable && depositable.error && (
              <p className="wnf-muted">Could not load the list of depositable coins — deposit and withdraw are unavailable right now.</p>
            )}
          </div>
        )}
      </PageState>

      <h2>Transactions</h2>
      <PageState loading={txLoading} error={txError ? describeLoadError(txError, 'transactions') : null}>
        {txs.length === 0 ? (
          <p className="wnf-muted">No transactions found</p>
        ) : (
          <>
            <Table columns={columns} rows={txs} rowKey={(r) => r.id} initialSort={{ key: 'createdAt', dir: 'desc' }} />
            {cursor && (
              <p>
                <button type="button" onClick={() => void loadMore()}>
                  Load more
                </button>
              </p>
            )}
          </>
        )}
      </PageState>
    </div>
  )
}
